import { useState, useEffect, useMemo, useContext } from 'react';
import { isEmptyOrSpaces } from '../../../../../helpers/StringHelper';
import { ProjectService } from '../../../../../services';
import { ProjectSourcesContext } from '../../../../../context/ProjectSources';
import useQuery from '../../../../../helpers/useQuery';
import { useNavigate } from 'react-router-dom';

export const useProjectSources = ({ user, conf, t = (s) => s }) => {
  const { form = {}, data = {}, group, onValuesChange } = conf;
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState();
  const push = useNavigate();
  const query = useQuery();
  const [mode, setMode] = useState(query.get('mode') === 'new' ? 'new' : 'default');
  const { info, setInfo } = useContext(ProjectSourcesContext);
  const { authorId } = data;
  const [unsavedUsers, setUnsavedUsers] = useState({ sources: [], clients: [] });
  const items = useMemo(() => group === 'informationSources' ? (data.sources || []) : (data.clients || []), [data, group]);

  useEffect(() => {
    if (query.get('mode') === 'new') {
      push(window.location.pathname);
    }
  }, []);

  useEffect(() => {
    setSearch('');
    if (items[0]) {
      const author = items.find((item) => authorId === item.authorId) || items[0];
      setSelectedItem(author?.authorId);
    } else {
      setSelectedItem(undefined);
    }
  }, [group]);

  const _update = (_new) => {
    if (typeof onValuesChange === 'function') {
      if (group === 'informationSources') {
        onValuesChange({ sources: _new }, { ...data, sources: _new });
      } else {
        onValuesChange({ clients: _new }, { ...data, clients: _new });
      }
    }
  }

  const onClickNew = () => setMode('new');
  const onCloseNew = () => setMode('default');

  const onAddNew = (data = {}, formData = {}) => {
    const _new = [...items, formData];
    _update(_new);

    setUnsavedUsers((prev) => {
      return {
        sources: [...prev.sources, ...(group === 'informationSources' ? [data.id] : [])],
        clients: [...prev.clients, ...(group === 'informationSources' ? [] : [data.id])],
      }
    });
    setInfo((prev) => [...prev, data]);
    setSelectedItem(data.id);
    setMode('default');
  };

  const mappedItems = useMemo(() => {
    const _items = items.map((item) => {
      const fromInfo = info.find((i) => i.id === item.authorId);
      const isOwnData = user.company?.id === item.authorId;
      return { ...item, data: fromInfo, isOwnData }
    });
    const _ownData = _items.find((it) => it.isOwnData);
    return _ownData ?
      [_ownData, ..._items.filter((it) => !it.isOwnData)]
      : _items;
  }, [info, items, user]);

  const filteredItems = useMemo(() => {
    if (isEmptyOrSpaces(search)) {
      return mappedItems;
    }

    return mappedItems.filter((item) => {
      const name = item.sourceName || item.name || '';

      const condition = name.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1
        || (item.data?.name || '').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1
        || (item.data?.email || '').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1
        || (item.data?.datastakeId || '').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1;

      if (item.isOwnData) {
        return condition || t('Own Data').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1;
      }

      return condition;
    });
  }, [mappedItems, search, t]);

  const sorted = useMemo(() => {
    if (Array.isArray(filteredItems)) {
      return filteredItems.sort((a, b) => {
        const valA = a.status === 'approved' ? 3 : a.status === 'pending' ? 2 : 1;
        const valB = b.status === 'approved' ? 3 : b.status === 'pending' ? 2 : 1;
        return valB - valA;
      }).map((item) => {
        const checkUnsaved = group === 'informationSources' ? unsavedUsers.sources : unsavedUsers.clients;
        if (checkUnsaved.includes(item.authorId)) {
          return { ...item, status: 'unsaved' };
        }
        return item;
      });
    }
    return [];
  }, [filteredItems, unsavedUsers, group]);

  const selected = useMemo(() => {
    if (selectedItem) {
      const item = mappedItems.find((_item) => _item.authorId === selectedItem);
      if (item) {
        const checkUnsaved = group === 'informationSources' ? unsavedUsers.sources : unsavedUsers.clients;
        if (checkUnsaved.includes(item.authorId)) {
          return { ...item, status: 'unsaved' };
        }
        return item;
      }
    }
  }, [mappedItems, selectedItem, group, unsavedUsers]);

  useEffect(() => {
    const getData = async () => {
      try {
        const _data = [];
        const _filteredItems = items.filter((i) => !info.find((item) => item.id === i.authorId));

        if (_filteredItems.length) {
          for (let i = 0; i < _filteredItems.length; i++) {
            const item = items[i];
            const res = await ProjectService.getLinking(item.authorId, data.id, item.role);
            if (res.data) {
              _data.push(res.data.data);
            }
          }
        }

        setInfo([...info, ..._data]);
      } catch (err) {
        console.log(err);
      }
    }

    getData();
  }, [group]);

  const shareInfoOptions = group === 'informationSources'
    ? form.options?.sources?.inputs?.shareInfo?.options
    : form.options?.clients?.inputs?.shareInfo?.options;

  const inputs = group === 'informationSources'
    ? form.options?.sources?.inputs
    : form.options?.clients?.inputs;

  const onChangeShareInfo = (shareInfo = []) => {
    const _new = items.map((it) => {
      if (it.authorId === selectedItem) {
        return { ...it, shareInfo }
      }
      return it;
    });
    _update(_new);
  };

  const onChangeSourceName = (sourceName = []) => {
    const _new = items.map((it) => {
      if (it.authorId === selectedItem) {
        return { ...it, sourceName }
      }
      return it;
    });
    _update(_new);
  };

  const onChangeStatus = (status) => {
    const _new = items.map((it) => {
      if (it.authorId === selectedItem) {
        return { ...it, status }
      }
      return it;
    });
    _update(_new);
  };

  const onChangeDisable = (disabled) => {
    const _new = items.map((it) => {
      if (it.authorId === selectedItem) {
        return { ...it, disabled }
      }
      return it;
    });
    _update(_new);
  };

  const onDelete = () => {
    const _new = items.map((it) => {
      if (it.authorId === selectedItem) {
        return { ...it, status: 'rejected' };
      }

      return it;
    });
    _update(_new);
  };

  return {
    form,
    mode,
    search,
    onDelete,
    selected,
    setSearch,
    selectedItem,
    setSelectedItem,
    onChangeDisable,
    shareInfoOptions,
    onChangeShareInfo,
    filteredItems,
    onClickNew,
    onCloseNew,
    onAddNew,
    group,
    inputs,
    onChangeStatus,
    onChangeSourceName,
    sorted,
    mappedItems,
  }
}
